// Google Maps for every map in order-admin (route detail, route wizard, DN driver
// position, order delivery location). Same loader and key as de-network-admin's
// google-map component, so one browser key covers the platform.
//
// Leaflet was replaced 2026-11-02: markers are React children (OverlayViewF)
// styled by google-map.module.css instead of HTML strings with inline styles.

import { Box, Skeleton, Text, useMantineTheme } from "@mantine/core"
import { GoogleMap, InfoWindowF, OverlayView, OverlayViewF, useJsApiLoader } from "@react-google-maps/api"
import { ReactNode, useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import classes from "./google-map.module.css"

declare global {
  interface Window {
    __ENV__?: { GOOGLE_MAPS_API_KEY?: string }
  }
}

// Runtime env first (window.__ENV__), then the CRA build env, then the platform key
// de-network-admin / de-partner-admin already ship with.
const GOOGLE_MAPS_API_KEY =
  (typeof window !== "undefined" && window.__ENV__?.GOOGLE_MAPS_API_KEY) ||
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  "AIzaSyBkAxH_ttCvI_YzjGwskVU1KD2F2UDrwBs"

// Delivery maps are about stops and roads: hide shops, hotels and transit icons.
const MAP_STYLES: google.maps.MapTypeStyle[] = [
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
]

// Must be a stable reference, otherwise useJsApiLoader reloads the script on every render.
const LIBRARIES: "places"[] = []

export type LatLng = { lat: number; lng: number }

/** Sukhbaatar Square, Ulaanbaatar: the view when nothing has coordinates. */
export const UB_CENTER: LatLng = { lat: 47.9184, lng: 106.9177 }

export function useGoogleMaps() {
  return useJsApiLoader({ id: "google-map-script", googleMapsApiKey: GOOGLE_MAPS_API_KEY, libraries: LIBRARIES })
}

/** Theme colour as a hex string: Google draws polylines on a canvas, so CSS variables do not reach it. */
export function useMapColor(color: string, shade = 6): string {
  const theme = useMantineTheme()
  const palette = theme.colors[color === "brand" ? theme.primaryColor : color] ?? theme.colors.gray
  return palette[shade]
}

/** Dashed stroke: Google has no dashArray, the dash is a repeated line symbol. */
export function dashedLine(color: string, repeat = "12px"): google.maps.PolylineOptions {
  return {
    strokeOpacity: 0,
    icons: [{ icon: { path: "M 0,-1 0,1", strokeOpacity: 1, strokeColor: color, scale: 3 }, offset: "0", repeat }],
  }
}

interface MapCanvasProps {
  height: number | string
  /** Initial view; after mount the map is moved only by fitPoints or the user. */
  center: LatLng
  zoom: number
  /** Re-fit whenever this set of points changes (one point = centre at zoom 14). */
  fitPoints?: LatLng[]
  onClick?: (position: LatLng) => void
  /** Wheel zoom without Ctrl; off for maps that sit inside a scrolling page. */
  scrollWheel?: boolean
  children?: ReactNode
}

export function MapCanvas({ height, center, zoom, fitPoints, onClick, scrollWheel = true, children }: MapCanvasProps) {
  const { t } = useTranslation(["delivery"])
  const { isLoaded, loadError } = useGoogleMaps()
  const [map, setMap] = useState<google.maps.Map | null>(null)

  const onLoad = useCallback(
    (m: google.maps.Map) => {
      m.setCenter(center)
      m.setZoom(zoom)
      setMap(m)
    },
    // Initial view only: later changes must not snap the map back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )
  const onUnmount = useCallback(() => setMap(null), [])

  const fitKey = (fitPoints ?? []).map((p) => `${p.lat},${p.lng}`).join("|")
  useEffect(() => {
    if (!map || !fitPoints || fitPoints.length === 0) return
    if (fitPoints.length === 1) {
      map.panTo(fitPoints[0])
      map.setZoom(14)
      return
    }
    const bounds = new google.maps.LatLngBounds()
    fitPoints.forEach((p) => bounds.extend(p))
    map.fitBounds(bounds, 40)
    // Two stops next door would otherwise zoom to street level.
    const once = google.maps.event.addListenerOnce(map, "idle", () => {
      if ((map.getZoom() ?? 0) > 15) map.setZoom(15)
    })
    return () => google.maps.event.removeListener(once)
    // fitKey is the identity of fitPoints.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, fitKey])

  if (loadError) {
    return (
      <Box className={classes.state} h={height}>
        <Text size="sm" c="var(--nw-text-2)">
          {t("delivery:googleMap.loadError")}
        </Text>
      </Box>
    )
  }
  if (!isLoaded) return <Skeleton h={height} radius={0} />

  return (
    <GoogleMap
      mapContainerClassName={classes.canvas}
      mapContainerStyle={{ height }}
      onLoad={onLoad}
      onUnmount={onUnmount}
      onClick={(e) => e.latLng && onClick?.({ lat: e.latLng.lat(), lng: e.latLng.lng() })}
      options={{
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        clickableIcons: false,
        gestureHandling: scrollWheel ? "greedy" : "cooperative",
        styles: MAP_STYLES,
      }}
    >
      {children}
    </GoogleMap>
  )
}

const centerOn = (width: number, height: number) => ({ x: -width / 2, y: -height / 2 })

interface HtmlMarkerProps {
  position: LatLng
  onClick?: () => void
  zIndex?: number
  children: ReactNode
}

/** A React element pinned by its centre to a coordinate. */
export function HtmlMarker({ position, onClick, zIndex, children }: HtmlMarkerProps) {
  return (
    <OverlayViewF
      position={position}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={centerOn}
      zIndex={zIndex}
    >
      <Box
        className={classes.markerHit}
        data-clickable={onClick ? true : undefined}
        onClick={(e) => {
          e.stopPropagation()
          onClick?.()
        }}
      >
        {children}
      </Box>
    </OverlayViewF>
  )
}

/** Popup above a marker; `offset` = marker radius in px. */
export function MapPopup({ position, offset = 16, onClose, children }: { position: LatLng; offset?: number; onClose: () => void; children: ReactNode }) {
  return (
    <InfoWindowF position={position} onCloseClick={onClose} options={{ pixelOffset: new google.maps.Size(0, -offset) }}>
      <Box className={classes.popup}>{children}</Box>
    </InfoWindowF>
  )
}
